import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { CURRENCIES, INVOICE_TYPES } from '@altyor-kronos/shared-types';
import type { Invoice } from '@altyor-kronos/shared-types';
import {
  validateInvoiceForm,
  type InvoiceFormValues,
} from './invoiceValidators';
import { useResponsive } from '../../hooks/useResponsive';

export interface ExistingInvoice {
  id: string;
  data: Invoice;
}

interface Props {
  open: boolean;
  editing: ExistingInvoice | null;
  designationName: string;
  onSubmit: (values: {
    invoiceNumber: string;
    invoiceDate: Date;
    invoiceAmount: number;
    paymentDate: Date | null;
    type: (typeof INVOICE_TYPES)[number];
    currency: (typeof CURRENCIES)[number];
  }) => Promise<void>;
  onClose: () => void;
}

function toDateInput(ts: Invoice['invoiceDate'] | undefined): string {
  if (!ts) return '';
  const d = ts.toDate();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
}

export default function InvoiceDialog({
  open,
  editing,
  designationName,
  onSubmit,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    reset,
    setError,
  } = useForm<InvoiceFormValues>({
    defaultValues: {
      invoiceNumber: '',
      invoiceDate: '',
      invoiceAmount: '',
      paymentDate: '',
      type: '',
      currency: 'EUR',
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        invoiceNumber: editing?.data.invoiceNumber ?? '',
        invoiceDate: toDateInput(editing?.data.invoiceDate),
        invoiceAmount: editing ? String(editing.data.invoiceAmount) : '',
        paymentDate: toDateInput(editing?.data.paymentDate),
        type: editing?.data.type ?? '',
        currency: editing?.data.currency ?? 'EUR',
      });
    }
  }, [open, editing, reset]);

  const onFormSubmit = handleSubmit(async (values) => {
    const v = validateInvoiceForm(values);
    let hadError = false;
    for (const key of Object.keys(v) as Array<keyof InvoiceFormValues>) {
      if (v[key]) {
        setError(key, { message: v[key] });
        hadError = true;
      }
    }
    if (hadError) return;
    const invoiceDate = parseDate(values.invoiceDate);
    const paymentDate = values.paymentDate ? parseDate(values.paymentDate) : null;
    await onSubmit({
      invoiceNumber: values.invoiceNumber.trim(),
      invoiceDate,
      invoiceAmount: Number(values.invoiceAmount),
      paymentDate,
      type: values.type as (typeof INVOICE_TYPES)[number],
      currency: values.currency as (typeof CURRENCIES)[number],
    });
    onClose();
  });

  return (
    <Dialog open={open} onClose={onClose} fullScreen={isMobile} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editing
          ? t('finance.invoice.edit', { designation: designationName })
          : t('finance.invoice.new', { designation: designationName })}
      </DialogTitle>
      <form onSubmit={onFormSubmit} noValidate>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(2, 1fr)', mt: 1 }}>
            <TextField
              label={t('finance.invoice.number')}
              fullWidth
              autoFocus
              {...register('invoiceNumber')}
              error={Boolean(errors.invoiceNumber)}
              helperText={errors.invoiceNumber?.message}
            />
            <Controller
              control={control}
              name="type"
              render={({ field }) => (
                <TextField
                  select
                  label={t('finance.invoice.type')}
                  fullWidth
                  {...field}
                  error={Boolean(errors.type)}
                  helperText={errors.type?.message}
                >
                  <MenuItem value="">—</MenuItem>
                  {INVOICE_TYPES.map((ty) => (
                    <MenuItem key={ty} value={ty}>
                      {t(`finance.invoice.types.${ty}`)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <TextField
              label={t('finance.invoice.date')}
              type="date"
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              {...register('invoiceDate')}
              error={Boolean(errors.invoiceDate)}
              helperText={errors.invoiceDate?.message}
            />
            <TextField
              label={t('finance.invoice.paymentDate')}
              type="date"
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              {...register('paymentDate')}
              error={Boolean(errors.paymentDate)}
              helperText={errors.paymentDate?.message}
            />
            <TextField
              label={t('finance.invoice.amount')}
              type="number"
              fullWidth
              slotProps={{ htmlInput: { step: '0.01', min: '0' } }}
              {...register('invoiceAmount')}
              error={Boolean(errors.invoiceAmount)}
              helperText={errors.invoiceAmount?.message}
            />
            <Controller
              control={control}
              name="currency"
              render={({ field }) => (
                <TextField
                  select
                  label={t('finance.invoice.currency')}
                  fullWidth
                  {...field}
                  error={Boolean(errors.currency)}
                  helperText={errors.currency?.message}
                >
                  {CURRENCIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="outlined" color="secondary" disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function parseDate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0);
}
